/**
 * Pure helpers for the route-away picker's role targets (Jess, QT-10036:
 * "add a generic person, such as 'appropriate sales regional' or
 * 'appropriate CS rep', highlighted based on the data Merlin already has for
 * that account"). Kept out of the component so the composition rules are
 * testable without a DOM.
 */
import type { DoorRoleTargetRead, RouteRole } from '../../services/qcpInvestigations';

/**
 * The `route_to` text to send: the chosen role tokens first (the backend
 * resolves them from the thread's account), then whatever was typed, joined
 * with the same comma the backend splits on. Empty when nothing was chosen.
 */
export function composeRouteTo(
  roles: DoorRoleTargetRead[],
  selected: ReadonlySet<RouteRole>,
  typed: string,
): string {
  const tokens = roles.filter((r) => selected.has(r.role)).map((r) => r.token);
  const name = typed.trim();
  return [...tokens, ...(name ? [name] : [])].join(', ');
}

/** "Account's sales rep → Cliff Platt", or the label alone when unresolved. */
export function roleChipText(role: DoorRoleTargetRead): string {
  return role.available && role.resolved_name
    ? `${role.label} → ${role.resolved_name}`
    : role.label;
}

/**
 * The one-liner under a chip: how the data got there for an available role
 * ("via DO Dornseif · cplatt@…"), or why it is greyed out.
 */
export function roleChipHint(role: DoorRoleTargetRead): string | null {
  if (!role.available) return role.reason;
  const parts = [role.detail, role.resolved_email].filter(
    (p): p is string => typeof p === 'string' && p.length > 0,
  );
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** Toggle a role in the selection (immutable). */
export function toggleRole(selected: ReadonlySet<RouteRole>, role: RouteRole): Set<RouteRole> {
  const next = new Set(selected);
  if (next.has(role)) next.delete(role);
  else next.add(role);
  return next;
}
